import type { VisitTypeId } from '../services/api/types.gen';
import { daysBetween, formatDateToISO, parseISOToLocalDate } from './dateUtils';

// This mirrors the backend classifier `episode_boundary_for_visit_type` in
// backend/app/models/enum/visit.py. Keep the two in sync when visit types change.

/**
 * Visit types whose scheduled date defines the episode START date: Start of Care
 * and Recertification across every discipline (SN/PT/OT/ST), legacy and enhanced.
 * Resumption of Care is intentionally excluded: it resumes care within an existing
 * episode, so a ROC date matching the start is coincidental, not definitional.
 */
export const EPISODE_START_DEFINING_VISIT_TYPES = new Set<VisitTypeId>([
  'OASIS_E2_START_OF_CARE',
  'OASIS_E2_START_OF_CARE_ENHANCED',
  'OASIS_E2_START_OF_CARE_PT',
  'OASIS_E2_START_OF_CARE_PT_ENHANCED',
  'OASIS_E2_START_OF_CARE_OT',
  'OASIS_E2_START_OF_CARE_OT_ENHANCED',
  'OASIS_E2_START_OF_CARE_ST',
  'OASIS_E2_START_OF_CARE_ST_ENHANCED',
  'OASIS_E2_RECERTIFICATION',
  'OASIS_E2_RECERTIFICATION_ENHANCED',
  'OASIS_E2_RECERTIFICATION_PT',
  'OASIS_E2_RECERTIFICATION_PT_ENHANCED',
  'OASIS_E2_RECERTIFICATION_OT',
  'OASIS_E2_RECERTIFICATION_OT_ENHANCED',
  'OASIS_E2_RECERTIFICATION_ST',
  'OASIS_E2_RECERTIFICATION_ST_ENHANCED',
]);

/**
 * Visit types whose scheduled date defines the episode END date: Discharge,
 * Transfer, and Death across every discipline (SN/PT/OT/ST), legacy and enhanced.
 */
export const EPISODE_END_DEFINING_VISIT_TYPES = new Set<VisitTypeId>([
  'OASIS_E2_DISCHARGE',
  'OASIS_E2_DISCHARGE_ENHANCED',
  'OASIS_E2_DISCHARGE_NON_BILLABLE',
  'OASIS_E2_DISCHARGE_PT',
  'OASIS_E2_DISCHARGE_PT_ENHANCED',
  'OASIS_E2_DISCHARGE_PT_NON_BILLABLE',
  'OASIS_E2_DISCHARGE_OT',
  'OASIS_E2_DISCHARGE_OT_ENHANCED',
  'OASIS_E2_DISCHARGE_OT_NON_BILLABLE',
  'OASIS_E2_DISCHARGE_ST',
  'OASIS_E2_DISCHARGE_ST_ENHANCED',
  'OASIS_E2_DISCHARGE_ST_NON_BILLABLE',
  'OASIS_E2_TRANSFER',
  'OASIS_E2_TRANSFER_ENHANCED',
  'OASIS_E2_TRANSFER_PT',
  'OASIS_E2_TRANSFER_PT_ENHANCED',
  'OASIS_E2_TRANSFER_OT',
  'OASIS_E2_TRANSFER_OT_ENHANCED',
  'OASIS_E2_TRANSFER_ST',
  'OASIS_E2_TRANSFER_ST_ENHANCED',
  'OASIS_E2_DEATH',
  'OASIS_E2_DEATH_ENHANCED',
  'OASIS_E2_DEATH_PT',
  'OASIS_E2_DEATH_PT_ENHANCED',
  'OASIS_E2_DEATH_OT',
  'OASIS_E2_DEATH_OT_ENHANCED',
  'OASIS_E2_DEATH_ST',
  'OASIS_E2_DEATH_ST_ENHANCED',
]);

/** Which episode boundary a visit's scheduled date is aligned with. */
export type EpisodeBoundary = 'start' | 'end';

const isStartDefining = (visitType: string): boolean =>
  EPISODE_START_DEFINING_VISIT_TYPES.has(visitType as VisitTypeId);

const isEndDefining = (visitType: string): boolean =>
  EPISODE_END_DEFINING_VISIT_TYPES.has(visitType as VisitTypeId);

/**
 * Classify a visit reschedule against the episode boundaries.
 *
 * Returns 'start' when the visit defines the episode start (SOC/REC) and its
 * current scheduled date equals the episode start date; 'end' when the visit
 * defines the episode end (DC/TRN/DTH) and its current scheduled date equals the
 * episode end date; otherwise null. Comparison is on the raw ISO "YYYY-MM-DD"
 * strings, which are directly comparable for equality.
 *
 * The date arguments are the values BEFORE the move, so callers can decide
 * whether to offer moving the matching episode boundary to the new visit date.
 */
export function classifyEpisodeBoundaryReschedule(params: {
  visitType: string | null | undefined;
  currentScheduledDate: string | null | undefined;
  episodeStartDate: string | null | undefined;
  episodeEndDate: string | null | undefined;
}): EpisodeBoundary | null {
  const { visitType, currentScheduledDate, episodeStartDate, episodeEndDate } =
    params;
  if (!visitType || !currentScheduledDate) {
    return null;
  }
  if (
    isStartDefining(visitType) &&
    episodeStartDate &&
    currentScheduledDate === episodeStartDate
  ) {
    return 'start';
  }
  if (
    isEndDefining(visitType) &&
    episodeEndDate &&
    currentScheduledDate === episodeEndDate
  ) {
    return 'end';
  }
  return null;
}

/**
 * Longest episode the backend will lengthen into, in *inclusive* days. Mirrors
 * `STANDARD_EPISODE_LENGTH_DAYS` in backend/app/crud/episode.py.
 *
 * Use this only for user-facing copy. Date math works on day *gaps*
 * (`end - start`), which are one less than the inclusive length — compare and
 * offset with `MAX_EPISODE_END_DATE_OFFSET` instead.
 */
export const MAX_EPISODE_LENGTH_DAYS = 60;

/**
 * Days after the start date for the latest legal end date (59), and equivalently
 * the largest legal `end - start` gap. Mirrors `STANDARD_EPISODE_END_DATE_OFFSET`
 * in backend/app/crud/episode.py.
 */
export const MAX_EPISODE_END_DATE_OFFSET = MAX_EPISODE_LENGTH_DAYS - 1;

/** The date range and status of one of the patient's episodes. */
export type PatientEpisodeDates = {
  id: string;
  start_date: string;
  end_date: string;
  status: string;
};

/** Selectable ISO "YYYY-MM-DD" range, `null` where the bound is unconstrained. */
export type BoundaryRescheduleBounds = {
  minDate: string | null;
  maxDate: string | null;
};

/** Shift an ISO "YYYY-MM-DD" date by whole days; calendar arithmetic only. */
const shiftYmd = (ymd: string, days: number): string | null => {
  const parsed = parseISOToLocalDate(ymd);
  if (!parsed) return null;
  const shifted = new Date(parsed);
  shifted.setDate(shifted.getDate() + days);
  return formatDateToISO(shifted);
};

/**
 * Dates a boundary-defining visit (SOC/REC on the start, DC/TRN/DTH on the end)
 * may be moved to, given that moving it also moves the episode boundary.
 *
 * Mirrors the rules `sync_episode_date_to_visit` enforces, so dates the backend
 * would reject are disabled in the picker instead of failing after the fact:
 *
 * - end must stay after start, so the moving boundary stops one day short of the
 *   fixed one;
 * - the episode may not be lengthened past 60 inclusive days (an `end - start`
 *   gap of 59) — a legacy episode already longer than that may keep its current
 *   length but not grow;
 * - the new range must not overlap another non-cancelled episode of the same
 *   patient, which bounds the move at the neighbouring episode's edge.
 *
 * Locked billing cycles and stranded child records are not expressible as a
 * contiguous range and stay server-side (a 400 toast and the 409 impact
 * confirmation respectively).
 */
export function boundaryRescheduleBounds(params: {
  boundary: EpisodeBoundary;
  episodeId: string;
  episodeStartDate: string;
  episodeEndDate: string;
  /** All of the patient's episodes, including the one being moved. */
  patientEpisodes: PatientEpisodeDates[];
}): BoundaryRescheduleBounds {
  const {
    boundary,
    episodeId,
    episodeStartDate,
    episodeEndDate,
    patientEpisodes,
  } = params;

  const start = parseISOToLocalDate(episodeStartDate);
  const end = parseISOToLocalDate(episodeEndDate);
  if (!start || !end) {
    return { minDate: null, maxDate: null };
  }
  // An episode already over the cap may shift or shrink but never grow, so the
  // budget is its own length rather than the standard one. Both sides are day
  // gaps, so the cap is the end-date offset (59), not the inclusive length (60).
  const maxLength = Math.max(
    MAX_EPISODE_END_DATE_OFFSET,
    daysBetween(start, end)
  );

  // Cancelled episodes are retired and do not block a date range; neither does
  // the episode being moved.
  const siblings = patientEpisodes.filter(
    episode => episode.id !== episodeId && episode.status !== 'cancelled'
  );

  if (boundary === 'start') {
    // The end date is fixed; the start may move within the length budget, up to
    // the day before the end, and no earlier than the day after the closest
    // episode that ends before this one starts.
    let minDate = shiftYmd(episodeEndDate, -maxLength);
    for (const sibling of siblings) {
      // Skips siblings the start cannot reach because the end date is fixed, and
      // any that already overlap this window — pre-existing data no date choice
      // here can fix, and bounding on them would disable the current date.
      if (sibling.end_date >= episodeStartDate) continue;
      const dayAfterSibling = shiftYmd(sibling.end_date, 1);
      if (dayAfterSibling && (!minDate || dayAfterSibling > minDate)) {
        minDate = dayAfterSibling;
      }
    }
    return { minDate, maxDate: shiftYmd(episodeEndDate, -1) };
  }

  // The start date is fixed; the end may move within the length budget, from the
  // day after the start, and no later than the day before the closest episode
  // that begins after this one ends.
  let maxDate = shiftYmd(episodeStartDate, maxLength);
  for (const sibling of siblings) {
    // Mirror of the start branch: skips unreachable and already-overlapping ones.
    if (sibling.start_date <= episodeEndDate) continue;
    const dayBeforeSibling = shiftYmd(sibling.start_date, -1);
    if (dayBeforeSibling && (!maxDate || dayBeforeSibling < maxDate)) {
      maxDate = dayBeforeSibling;
    }
  }
  return { minDate: shiftYmd(episodeStartDate, 1), maxDate };
}
